import {FC, useCallback, useMemo, useState} from 'react'
import {Form, Formik} from 'formik'
import {ToastContainer} from 'react-toastify'
import DataTable, {TableColumn} from '../../../shared/components/DataTable'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import DateInput from '../../../shared/components/DateInput'
import CheckboxInput from '../../../shared/components/CheckboxInput'
import {showErrorToast} from '../../../shared/components/ToastUtils'
import {fetchCustomerList} from './Api'
import {ICustomerModel} from './CustomerModel'
import {
  CUSTOMER_LIST_ACTIVE,
  CUSTOMER_LIST_CARI_TIPI,
  CUSTOMER_LIST_DATE_FILTER,
  ICustomerListFilter,
  ICustomerListRow,
  initialCustomerListFilter,
  mapCustomerToListRow,
  validationCustomerListFilter,
} from './CustomerListModel'

function buildColumns(): TableColumn<ICustomerListRow>[] {
  const col = (key: keyof ICustomerListRow, label: string): TableColumn<ICustomerListRow> => ({
    key,
    label,
    className: 'text-gray-800 min-w-125px',
  })
  return [
    col('firstName', 'Ad'),
    col('lastName', 'Soyad'),
    col('cepTel', 'Cep Tel'),
    col('tel2', 'Tel2'),
    col('tel3', 'Tel3'),
    col('emailAddress', 'Mail Adresi'),
    col('tcKimlikNo', 'Tc Kimlik No'),
    col('taxNumber', 'Vergi Numarası'),
    col('taxOffice', 'Vergi Dairesi'),
    col('address', 'Adres'),
    col('district', 'İlçe'),
    col('city', 'Şehir'),
    col('islemSube', 'İşlem Şube'),
    col('bakiyeSube', 'Bakiye Şube'),
    col('soyadYedek', 'Soyad'),
    col('onem', 'Önem'),
    col('ozelKod', 'Özel Kod'),
    col('uyari', 'Uyarı'),
    col('uyariTarih', 'Uyarı Tarih'),
    col('id', 'ID'),
    col('yetkili', 'Yetkili'),
    col('yetkiliTel', 'Yetkili Tel'),
    col('yetkiliMail', 'Yetkili Mail'),
    col('musteriTemsilcisi', 'Müşteri Temsilcisi'),
    col('dogumTarihi', 'Doğum Tarihi'),
    col('pasaportNo', 'Pasaport No'),
    col('unvan1', 'Ünvan1'),
    col('yetkili1', 'Yetkili1'),
    col('yetkiliTel1', 'Yetkili Tel1'),
    col('yetkiliMail1', 'Yetkili Mail1'),
    col('unvan2', 'Ünvan2'),
    col('yetkili2', 'Yetkili2'),
    col('yetkiliTel2', 'Yetkili Tel2'),
    col('yetkiliMail2', 'Yetkili Mail2'),
    col('unvan3', 'Ünvan3'),
    col('yetkili3', 'Yetkili3'),
    col('yetkiliTel3', 'Yetkili Tel3'),
    col('yetkiliMail3', 'Yetkili Mail3'),
  ]
}

export const CustomerListPage: FC = () => {
  const [rows, setRows] = useState<ICustomerListRow[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [totalElements, setTotalElements] = useState(0)
  const [pageSize] = useState(25)
  const defaultFilter = useMemo<ICustomerListFilter>(() => {
    const t = new Date()
    return {
      ...initialCustomerListFilter,
      startDate: t,
      endDate: t,
    }
  }, [])
  const [filterValues, setFilterValues] = useState<ICustomerListFilter>(defaultFilter)
  /** İlk açılışta liste boş; en az bir kez Filtrele sonrası sayfalama ve veri yüklenir */
  const [hasFiltered, setHasFiltered] = useState(false)
  const [filterOpen, setFilterOpen] = useState(true)

  const columns = useMemo(() => buildColumns(), [])

  const runFilter = useCallback(
    async (values: ICustomerListFilter, page: number) => {
      setLoading(true)
      try {
        const size = values.allRecords ? 500 : pageSize
        const useDates = values.dateFilterType === 'TRANSACTION'
        const start =
          useDates && values.startDate && !isNaN(values.startDate.getTime())
            ? values.startDate.toISOString().slice(0, 10)
            : undefined
        const end =
          useDates && values.endDate && !isNaN(values.endDate.getTime())
            ? values.endDate.toISOString().slice(0, 10)
            : undefined

        const res = await fetchCustomerList(
          {
            searchKeyword: values.cariAra.trim() || undefined,
            lastName: values.soyad.trim() || undefined,
            dateFilterType: values.dateFilterType,
            startDate: start,
            endDate: end,
            cariTipi: values.cariTipi !== 'ALL' ? values.cariTipi : undefined,
            activeStatus: values.activeStatus !== 'ALL' ? values.activeStatus : undefined,
            allRecords: values.allRecords,
          },
          page,
          size
        )
        const data = res.data.payload || res.data
        const payload = data as {
          content?: ICustomerModel[]
          totalPages?: number
          totalElements?: number
        }
        const content = payload?.content ?? []
        setRows(content.map((c) => mapCustomerToListRow(c)))
        setTotalPages(payload?.totalPages ?? 1)
        setTotalElements(payload?.totalElements ?? content.length)
      } catch (err: unknown) {
        const e = err as {response?: {status?: number}}
        if (e?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Müşteri listesi yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (!hasFiltered) return
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        runFilter(filterValues, newPage)
      }
    },
    [totalPages, filterValues, runFilter, hasFiltered]
  )

  const handleSubmit = async (values: ICustomerListFilter) => {
    setFilterValues(values)
    setCurrentPage(0)
    setHasFiltered(true)
    await runFilter(values, 0)
  }

  const datesDisabled = (values: ICustomerListFilter) => values.dateFilterType === 'IGNORE'

  return (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center gap-2 my-1'>
              <i className='bi bi-people fs-1 text-primary' aria-hidden />
              <h3 className='fw-bold m-0'>Müşteri Listeleme</h3>
            </div>
          </div>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFilterOpen((p) => !p)}
              aria-expanded={filterOpen}
            >
              <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                <span className='path1' />
                <span className='path2' />
              </i>
              {filterOpen ? 'Filtreleri gizle' : 'Filtreleri göster'}
            </button>
          </div>
        </div>

        <div className={`collapse ${filterOpen ? 'show' : ''}`}>
          <div className='card-body border-top pt-6'>
            <Formik
              initialValues={defaultFilter}
              enableReinitialize
              validationSchema={validationCustomerListFilter}
              onSubmit={handleSubmit}
            >
              {(formik) => (
                <Form className='form' id='customerListFilterForm'>
                  <div className='row g-5 mb-5'>
                    <TextInput
                      name='cariAra'
                      label='Cari Ara'
                      placeholder='[Ad, Soyad, TC Kimlik, Ehliyet No, Vergi No veya Telefon]'
                      wrapperClassName='col-12 col-xl-6'
                    />
                    <TextInput name='soyad' label='Soyad' placeholder='Soyad' wrapperClassName='col-12 col-md-6 col-xl-6' />
                    <SelectInput
                      name='dateFilterType' 
                      wrapperClassName='col-12 col-md-6 col-xl-2'
                      options={[...CUSTOMER_LIST_DATE_FILTER]}
                      firstItemLabel=''
                    />
                    <DateInput
                      name='startDate'
                      type='date' 
                      wrapperClassName='col-12 col-md-6 col-xl-2' 
                    />
                    <DateInput
                      name='endDate'
                      type='date' 
                      wrapperClassName='col-12 col-md-6 col-xl-2' 
                    /> 
                     <SelectInput
                      name='cariTipi'
                      label='Cari Tipi'
                      wrapperClassName='col-12 col-md-6 col-xl-6'
                      options={[...CUSTOMER_LIST_CARI_TIPI]}
                      firstItemLabel=''
                    />
                    <CheckboxInput
                      name='allRecords'
                      label='Hepsi (Tüm Kayıtlar)'
                      wrapperClassName='col-12 col-md-6 col-xl-6' 
                    />
                    <SelectInput
                      name='activeStatus'
                      label='Aktif/Pasif'
                      wrapperClassName='col-12 col-md-6 col-xl-6'
                      options={[...CUSTOMER_LIST_ACTIVE]}
                      firstItemLabel=''
                    />
                    
                  </div>
 

                  <div className='d-flex flex-wrap align-items-center gap-4'>
                    <button type='submit' className='btn btn-primary' disabled={loading}>
                      {loading ? (
                        <span className='indicator-progress' style={{display: 'block'}}>
                          Lütfen bekleyin...
                          <span className='spinner-border spinner-border-sm align-middle ms-2' />
                        </span>
                      ) : (
                        <>
                          <i className='bi bi-funnel me-2' aria-hidden />
                          Filtrele
                        </>
                      )}
                    </button>
                    <div className='border border-gray-300 rounded px-4 py-2 bg-light'>
                      <span className='text-gray-700 fw-semibold fs-7'>
                        Kayıt Sayısı: {totalElements}
                      </span>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        <div className='card-body pt-0'>
          <DataTable<ICustomerListRow>
            data={rows}
            columns={columns}
            actions={[]}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            loading={loading}
            emptyMessage={
              hasFiltered
                ? 'Kayıt bulunamadı'
                : 'Listeyi yüklemek için Filtrele butonuna basın.'
            }
            tableClassName='table-row-dashed fs-8 text-nowrap'
            showPagination={hasFiltered}
          />
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}
